import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Users, Layers, Calendar, UserPlus, MoreVertical, Copy, Trash2 } from 'lucide-react';
import type { ApprovalStatus, ApprovalWorkflow } from '../types';

const getStatusBadgeVariant = (status: ApprovalStatus) => {
  switch (status) {
    case 'Active':
      return 'default';
    case 'Draft':
      return 'secondary';
    case 'Inactive':
      return 'outline';
  }
};

const getStatusColor = (status: ApprovalStatus) => {
  switch (status) {
    case 'Active':
      return 'bg-green-500 hover:bg-green-600';
    case 'Draft':
      return 'bg-yellow-500 hover:bg-yellow-600';
    case 'Inactive':
      return 'bg-gray-500 hover:bg-gray-600';
  }
};

const formatDate = (date: Date): string => {
  const now = new Date();
  // Use floor instead of ceil to avoid showing "1 day ago" for same-day items
  const diffTime = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
};

interface WorkflowCardProps {
  approval: ApprovalWorkflow;
  onView: (workflowId: string) => void;
  onAssign: (workflowId: string, e: React.MouseEvent) => void;
  onDuplicate: (workflowId: string, e: React.MouseEvent) => void;
  onDelete: (workflowId: string) => void;
}

export const WorkflowCard: React.FC<WorkflowCardProps> = ({
  approval,
  onView,
  onAssign,
  onDuplicate,
  onDelete,
}) => (
  <Card
    className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-primary/50"
    onClick={() => onView(approval.id)}
  >
    <CardHeader className="pb-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <CardTitle className="text-lg font-semibold leading-tight">
            {approval.name}
          </CardTitle>
        </div>
        <div className="flex items-center gap-2">
          {approval.isDefault && (
            <Badge variant="secondary" className="text-xs py-0 h-4">
              Default
            </Badge>
          )}
          <Badge
            variant={getStatusBadgeVariant(approval.status)}
            className={
              approval.status === 'Active'
                ? getStatusColor(approval.status)
                : approval.status === 'Draft'
                ? getStatusColor(approval.status)
                : ''
            }
          >
            {approval.status}
          </Badge>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onDuplicate(approval.id, e);
                }}
              >
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(approval.id);
                }}
                className="text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      {/* Simplified metrics - inline icons */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <Layers className="h-3.5 w-3.5" />
          <span className="font-medium text-foreground">{approval.stages}</span>
          <span className="text-xs">stage{approval.stages !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" />
          <span className="font-medium text-foreground">{approval.assignedEmployees}</span>
          <span className="text-xs">employee{approval.assignedEmployees !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Last Modified */}
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Calendar className="h-3.5 w-3.5" />
        <span>Updated {formatDate(approval.lastModified)}</span>
      </div>

      {/* Assign Button */}
      <div className="pt-2 border-t">
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={(e) => onAssign(approval.id, e)}
          disabled={approval.status !== 'Active'}
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Assign to Users
        </Button>
      </div>
    </CardContent>
  </Card>
);
